import { useLayoutEffect, useRef } from 'react';

/** Toggle `.is-h-scrolling` when the table scroller has a horizontal overflow. */
export default function useTableHScrollPin(ref, enabled = true) {
  const attachment = useRef(null);
  // The table can mount after the loading screen. Inspect the committed ref on
  // each render, but only reconnect observers when the actual element changes.
  useLayoutEffect(() => {
    const el = enabled ? ref.current : null;
    if (attachment.current?.el === el) return;
    attachment.current?.cleanup();
    attachment.current = null;
    if (!el) return undefined;

    let raf = 0;
    const sync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!el.isConnected) return;
        if (el.clientWidth < 8) {
          el.classList.remove('is-h-scrolling');
          el.classList.remove('show-no-more');
          return;
        }
        const hadClass = el.classList.contains('is-h-scrolling');
        el.style.setProperty('--table-viewport-width', `${el.clientWidth}px`);
        const overflowing = el.scrollWidth - el.clientWidth > 2;
        el.classList.toggle('is-h-scrolling', overflowing);
        el.style.setProperty(
          '--table-scrollbar-y',
          `${Math.max(0, el.offsetWidth - el.clientWidth)}px`,
        );

        // Vertical scroll check: "No more..." indicator only shows when table content
        // actually overflows vertically (requires scrolling).
        // Sizing is kept stable from the start so the scrollbar does NOT shrink or jump after scrolling.
        const noMoreEl = el.querySelector('.no-more-inline');
        const noMoreH = noMoreEl && el.classList.contains('show-no-more') ? noMoreEl.offsetHeight : 0;
        const contentH = el.scrollHeight - noMoreH;
        const isScrollable = contentH - el.clientHeight > 2;
        el.classList.toggle('show-no-more', isScrollable);

        // Re-measure once after toggling column flex rules — scrollWidth may shrink.
        if (overflowing !== hadClass) {
          requestAnimationFrame(() => {
            if (!el.isConnected) return;
            el.classList.toggle('is-h-scrolling', el.scrollWidth - el.clientWidth > 2);
            el.style.setProperty(
              '--table-scrollbar-y',
              `${Math.max(0, el.offsetWidth - el.clientWidth)}px`,
            );
          });
        }
      });
    };

    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    const mo = new MutationObserver(sync);
    mo.observe(el, { childList: true, subtree: true });
    window.addEventListener('resize', sync);
    el.addEventListener('scroll', sync, { passive: true });
    const cleanup = () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', sync);
      el.removeEventListener('scroll', sync);
    };
    attachment.current = { el, cleanup };
  });
  useLayoutEffect(() => () => {
    attachment.current?.cleanup();
    attachment.current = null;
  }, []);
}
